import React, { forwardRef } from 'react';
import { Autocomplete as MuiAutocomplete, TextField } from '@mui/material';

interface AutocompleteProps {
	id?: string;
	options: AutocompleteOption[];
	label: string;
	placeholder?: string;
	value: any;
	onChange: any;
	getOptionLabel?: any;
	disabled?: boolean;
	multiple?: boolean;
	freeSolo?: boolean;
	filterOptions?: any;
	onInputChange?: any;
	helperText?: string;
	error?: boolean;
	required?: boolean;
	onBlur?: any;
	size?: 'small' | 'medium';
	InputLabelProps?: any;
	className?: string;
	renderTags?: any;
	renderOption?: any;
	renderInput?: any;
	isOptionEqualToValue?: any;
	sx?: any;
}
const Autocomplete = forwardRef((props: AutocompleteProps, ref) => {
	const {
		id = 'auto-complete-component',
		options,
		label,
		placeholder = 'Select',
		value,
		onChange,
		getOptionLabel = (option: string | AutocompleteOption) => {
			if (typeof option === 'string') {
				const matchingOption = options.find((opt) => opt.id === option);
				return matchingOption ? matchingOption.display : option;
			} else {
				return option.display;
			}
		},
		disabled,
		multiple = false,
		freeSolo = false,
		helperText,
		error = false,
		required = false,
		onBlur,
		size = 'small',
		InputLabelProps = {},
		className,
		renderTags,
		renderOption,
		renderInput,
		isOptionEqualToValue,
		sx,
	} = props;

	const handleInputChange = (event: any, newValue: string) => {
		if (props.onInputChange) {
			props.onInputChange(event, newValue);
		}
	};

	const filterOptions = props.filterOptions
		? props.filterOptions
		: (options, { inputValue }) => {
				const match = new RegExp(inputValue, 'i');
				return options
					.filter((option) => match.test(getOptionLabel(option)))
					.sort((a, b) => {
						// Place exact matches at the top
						if (getOptionLabel(a) === inputValue) {
							return -1;
						}
						if (getOptionLabel(b) === inputValue) {
							return 1;
						}
						// For other matches, sort by their occurrence in the label
						return (
							getOptionLabel(a).indexOf(inputValue) -
							getOptionLabel(b).indexOf(inputValue)
						);
					});
		  };

	const defaultRenderOption = (props, option) => {
		return (
			<li {...props} key={option.id}>
				{option.display}
			</li>
		);
	};

	const defaultIsOptionEqualToValue = (option, value) =>
		option.id === value.id ||
		option.id === value ||
		option.display === value;

	const defaultRenderInput = (params) => (
		<TextField
			{...params}
			label={label}
			placeholder={placeholder}
			error={error}
			helperText={helperText}
			required={required}
			InputLabelProps={InputLabelProps}
			size={'small'}
		/>
	);

	return (
		<MuiAutocomplete
			size={size}
			multiple={multiple}
			filterSelectedOptions
			id={id}
			options={options}
			getOptionLabel={getOptionLabel}
			renderOption={renderOption || defaultRenderOption}
			renderTags={renderTags}
			onReset={() => console.log('reset')}
			isOptionEqualToValue={
				isOptionEqualToValue || defaultIsOptionEqualToValue
			}
			onChange={onChange}
			value={value || null} // null means this will always be a controlled component
			sx={{ width: '100%', ...sx }}
			disabled={disabled}
			className={className}
			renderInput={renderInput || defaultRenderInput}
			onInputChange={handleInputChange}
			freeSolo={freeSolo}
			filterOptions={filterOptions}
			onBlur={onBlur}
			autoHighlight
		/>
	);
});
export default Autocomplete;

export interface AutocompleteOption {
	id: string;
	display: string;
	// Optional additional properties for more complex options
	firstName?: string;
	lastName?: string;
	email?: string;
	role?: string;
	type?: string;
	[key: string]: any; // Allow additional properties
}
